import React, {
    useState, useEffect, useRef, useMemo,
    useImperativeHandle, forwardRef,
} from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import SignatureInput from './SignatureInput';
import { FormData, NomineeData, ValidationErrors, GratuityNominationProps, FormValidateHandle } from './typesIN';
import { useFieldLock } from './useFieldLock';

const removeSpecialChars = (v: string) => v.replace(/[!#$%^&*()\-_=+;:'"<>/|\\{}[\]~`]/g, '');
const allowOnlyAlphabets = (v: string) => v.replace(/[^a-zA-Z\s.]/g, ''); // allow dot for initials
const ADDRESS_ALLOWED = /[^a-zA-Z0-9\s\-/#():,.&@]/g;
const sanitiseAddress = (v: string): string =>
    v.replace(ADDRESS_ALLOWED, '').replace(/-{2,}/g, '-').replace(/\/\*/g, '/').replace(/\*\//g, '/').slice(0, 200);
const sanitiseProportion = (v: string): string =>
    v.replace(/\D/g, '').replace(/^0+(\d)/, '$1').slice(0, 3);
const toDateOrUndefined = (v: any): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? undefined : d;
};
const calculateAge = (dob: any): number | null => {
    const d = toDateOrUndefined(dob);
    if (!d) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
};
const TODAY = new Date();

const ClearableCalendar = ({
    value, onChange, className, ...rest
}: {
    value: Date | undefined;
    onChange: (val: Date | null) => void;
    className?: string;
    [key: string]: any;
}) => (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '100%' }}>
        <Calendar
            value={value}
            onChange={(e) => onChange(e.value as Date | null)}
            className={className}
            style={{ width: '100%' }}
            {...rest}
        />
        {value && (
            <i
                className="pi pi-times"
                onClick={() => onChange(null)}
                style={{ position: 'absolute', right: '36px', cursor: 'pointer', color: '#6c757d', fontSize: '0.75rem', zIndex: 1 }}
            />
        )}
    </div>
);

const ageCheck = (v: unknown, label: string): string => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const n = Number(s);
    if (isNaN(n) || n < 1 || n > 120) return `${label} must be between 1 and 120`;
    return '';
};

const GratuityNomination = forwardRef<FormValidateHandle, GratuityNominationProps>(
    ({ onContinue, onDraftSave, onBack, initialData = {}, initialNominees = [], fieldEditability = {}, autoSignature = '' }, ref) => {
        const toast = useRef<Toast>(null);

        const [formData, setFormData] = useState<FormData>({ proportion: '100', ...initialData });

        useEffect(() => {
            if (initialData && Object.keys(initialData).length > 0) {
                setFormData(prev => {
                    const merged = { ...prev };
                    Object.entries(initialData).forEach(([key, val]) => {
                        const existing = prev[key];
                        const isEmpty = existing === undefined || existing === null || existing === '';
                        if (val !== undefined && val !== null && val !== '' && isEmpty) merged[key] = val;
                    });
                    return merged;
                });
            }
        }, [initialData]);

        // Auto-populate subscriber signature from Form2 whenever it changes
        useEffect(() => {
            if (autoSignature) {
                setFormData(prev => ({ ...prev, subscriberSignature: autoSignature }));
            }
        }, [autoSignature]);

        const ALWAYS_READONLY = ['toName', 'employeeName', 'department', 'postHeld', 'appointmentDate', 'place', 'date', 'finalDate', 'joiningLocation', 'establishmentAddress', 'designation', 'employerSignature'];
        const ALWAYS_EDITABLE = ['prefix', 'nomineeFullName', 'nomineeName', 'nomineeAddress', 'relationship', 'proportion', 'nomineeDob', 'nomineeAge', 'permanentAddress', 'religion', 'subscriberSignature', 'gender', 'maritalStatus'];
        const isLocked = useFieldLock(formData, fieldEditability, ALWAYS_READONLY, ALWAYS_EDITABLE);

        const [nominees, setNominees] = useState<NomineeData[]>(initialNominees);
        const [errors, setErrors] = useState<ValidationErrors>({});
        const [nomineeErrors, setNomineeErrors] = useState<{ [id: string]: ValidationErrors }>({});
        const [acknowledgement, setAcknowledgement] = useState(false);

        const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        useEffect(() => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(() => { onDraftSave?.(formData, nominees); }, 500);
            return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData, nominees]);

        useEffect(() => {
            if (nominees.length === 0 && formData.proportion !== '100')
                setFormData(prev => ({ ...prev, proportion: '100' }));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [nominees.length]);

        const nomineeAge = useMemo(() => calculateAge(formData.nomineeDob), [formData.nomineeDob]);
        const nomineeDobWasSet = useRef(!!toDateOrUndefined(formData.nomineeDob));

        useEffect(() => {
            if (nomineeAge !== null) {
                nomineeDobWasSet.current = true;
                setFormData(prev => ({ ...prev, nomineeAge: String(nomineeAge) }));
            } else if (nomineeDobWasSet.current) {
                nomineeDobWasSet.current = false;
                setFormData(prev => ({ ...prev, nomineeAge: '' }));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [nomineeAge]);

        const genderOptions = [{ label: 'Select', value: '' }, { label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' }];
        const maritalStatusOptions = [{ label: 'Select', value: '' }, { label: 'Unmarried', value: 'unmarried' }, { label: 'Married', value: 'married' }, { label: 'Divorced', value: 'divorced' }, { label: 'Widow', value: 'widow' }, { label: 'Widower', value: 'widower' }];
        const relationshipOptions = [{ label: 'Select', value: '' }, { label: 'Spouse', value: 'spouse' }, { label: 'Father', value: 'father' }, { label: 'Mother', value: 'mother' }, { label: 'Son', value: 'son' }, { label: 'Daughter', value: 'daughter' }];
        const prefixOptions = [{ label: 'Select', value: '' }, { label: 'Shri', value: 'Shri' }, { label: 'Shrimati', value: 'Shrimati' }, { label: 'Kumari', value: 'Kumari' }];

        const addNominee = () => setNominees(prev => prev.length >= 3 ? prev : [...prev, { id: Date.now().toString() }]);
        const removeNominee = (id: string) => {
            setNominees(prev => prev.filter(n => n.id !== id));
            setNomineeErrors(prev => { const u = { ...prev }; delete u[id]; return u; });
        };

        const handleInputChange = (field: string, value: any) => {
            const nameFields = ['nomineeName', 'subscriberSignature', 'religion'];
            const addressFields = ['address', 'nomineeAddress', 'permanentAddress', 'establishmentAddress', 'nomineeFullName'];
            if (typeof value === 'string') {
                if (addressFields.includes(field)) value = sanitiseAddress(value);
                else if (nameFields.includes(field)) value = allowOnlyAlphabets(value);
                else value = removeSpecialChars(value);
            }
            setFormData(prev => ({ ...prev, [field]: value }));
            if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        };

        const handleNomineeChange = (index: number, field: string, value: any) => {
            const nameFields = ['name'];
            const addressFields = ['address', 'fullName'];
            if (typeof value === 'string') {
                if (addressFields.includes(field)) value = sanitiseAddress(value);
                else if (nameFields.includes(field)) value = allowOnlyAlphabets(value);
                else value = removeSpecialChars(value);
            }
            const updated = [...nominees];
            const prevDob = updated[index].dob;
            updated[index] = { ...updated[index], [field]: value };
            if (field === 'dob') {
                const age = calculateAge(value);
                if (age !== null) {
                    updated[index].age = String(age);
                } else if (prevDob) {
                    // was set before, now cleared
                    updated[index].age = '';
                }
            }
            setNominees(updated);
            const id = nominees[index].id;
            if (nomineeErrors[id]?.[field]) setNomineeErrors(prev => ({ ...prev, [id]: { ...prev[id], [field]: '' } }));
        };

        const validateForm = (): boolean => {
            const e: ValidationErrors = {};
            const addrCheck = (v: unknown, label: string, required = true): string => {
                const s = String(v ?? '').trim();
                if (required && s.length === 0) return `${label} is required`;
                if (s.length > 0 && s.length < 10) return `${label} must be at least 10 characters`;
                if (String(v ?? '').length > 200) return `${label} must not exceed 200 characters`;
                return '';
            };
            const shareCheck = (v: unknown, label: string): string => {
                if (!v || String(v).trim() === '') return `${label} is required`;
                const n = Number(v);
                if (isNaN(n) || n <= 0) return `${label} must be greater than 0`;
                if (n > 100) return `${label} cannot exceed 100%`;
                return '';
            };

            // address is auto-populated/read-only — no validation needed
            if (!formData.prefix) e.prefix = 'Shri/Shrimati/Kumari is required';
            if (!formData.nomineeFullName) e.nomineeFullName = 'Name in full with address is required';
            if (!formData.relationship) e.relationship = 'Relationship is required';

            const nomineeDobVal = toDateOrUndefined(formData.nomineeDob);
            const nomineeAgeVal = String(formData.nomineeAge ?? '').trim();
            if (!nomineeDobVal && !nomineeAgeVal) {
                e.nomineeDob = 'Either Date of Birth or Age is required';
            } else if (!nomineeDobVal && nomineeAgeVal) {
                const ageErr = ageCheck(nomineeAgeVal, "Nominee's Age");
                if (ageErr) e.nomineeDob = ageErr;
            }

            if (nominees.length > 0) { const p = shareCheck(formData.proportion, 'Proportion'); if (p) e.proportion = p; }
            if (!formData.gender) e.gender = 'Gender is required';
            if (!formData.maritalStatus) e.maritalStatus = 'Marital Status is required';
            const pa = addrCheck(formData.permanentAddress, 'Permanent Address'); if (pa) e.permanentAddress = pa;
            if (!formData.subscriberSignature) e.subscriberSignature = 'Subscriber Signature is required';
            if (!acknowledgement) e.acknowledgement = 'Please confirm the acknowledgement';
            const na = addrCheck(formData.nomineeAddress, "Nominee's Address", false); if (na) e.nomineeAddress = na;
            setErrors(e);

            const nErrs: { [id: string]: ValidationErrors } = {}; let hasNE = false;
            nominees.forEach((nom, i) => {
                const ne: ValidationErrors = {}; const l = `Nominee ${i + 2}`;
                if (!nom.prefix) { ne.prefix = `${l} prefix required`; hasNE = true; }
                if (!nom.fullName) { ne.fullName = `${l} full name required`; hasNE = true; }
                const nDobVal = toDateOrUndefined(nom.dob);
                const nAgeVal = String(nom.age ?? '').trim();
                if (!nDobVal && !nAgeVal) {
                    ne.dob = `${l} DOB or age required`; hasNE = true;
                } else if (!nDobVal && nAgeVal) {
                    const ageErr = ageCheck(nAgeVal, `${l} Age`);
                    if (ageErr) { ne.dob = ageErr; hasNE = true; }
                }
                if (!nom.relationship) { ne.relationship = `${l} relationship required`; hasNE = true; }
                const sp = shareCheck(nom.proportion, `${l} proportion`); if (sp) { ne.proportion = sp; hasNE = true; }
                if (Object.keys(ne).length) nErrs[nom.id] = ne;
            });
            setNomineeErrors(nErrs);

            const total = (Number(formData.proportion) || 0) + nominees.reduce((s, n) => s + (Number(n.proportion) || 0), 0);
            if (!hasNE && Object.keys(e).length === 0 && total !== 100) {
                setErrors(prev => ({ ...prev, proportion: `All proportions must total 100% (currently ${total}%)` }));
                toast.current?.show({ severity: 'warn', summary: 'Incomplete Form', detail: 'Please fill in all mandatory fields before continuing.', life: 4000 });
                return false;
            }

            const isValid = Object.keys(e).length === 0 && !hasNE;
            if (!isValid) {
                toast.current?.show({ severity: 'warn', summary: 'Incomplete Form', detail: 'Please fill in all mandatory fields before continuing.', life: 4000 });
            }
            return isValid;
        };

        // ── Expose validate() to parent via ref ──────────────────────────────
        useImperativeHandle(ref, () => ({ validate: validateForm }));

        const handleContinue = () => {
            if (validateForm()) onContinue?.(formData, nominees);
            else window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return (
            <div className="form-container">
                <Toast ref={toast} position="top-right" />
                <h2 className="form-title">Gratuity Nomination Form</h2>

                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>To (Name)*</label>
                        <InputText disabled value={formData.toName as string || 'TTEC INDIA CUSTOMER SOLUTIONS PVT. LTD.'} />
                    </div>
                    <div className="form-field">
                        <label>Address*</label>
                        <InputText disabled value={formData.address as string || ''} placeholder="Auto populated" />
                    </div>
                </div>

                <div className="nominee-section">
                    <p className="nominee-instruction">If you want to add new people, please click the Add Nominee button.</p>

                    <div className="form-row white-bg two-column">
                        <div className="form-field full-width">
                            <label>Shri/Shrimati/Kumari*</label>
                            <Dropdown value={formData.prefix} options={prefixOptions} onChange={(e) => handleInputChange('prefix', e.value)} placeholder="Select" className={errors.prefix ? 'p-invalid' : ''} />
                            {errors.prefix && <small className="p-error">{errors.prefix}</small>}
                        </div>
                        <div className="form-field full-width">
                            <label>Name in full with full address of nominee(s)*</label>
                            <InputText placeholder="Input" value={formData.nomineeFullName as string || ''} onChange={(e) => handleInputChange('nomineeFullName', e.target.value)} maxLength={200} className={errors.nomineeFullName ? 'p-invalid' : ''} />
                            {errors.nomineeFullName && <small className="p-error">{errors.nomineeFullName}</small>}
                        </div>
                    </div>

                    <div className="form-row green-bg two-column">
                        <div className="form-field full-width">
                            <label>Full Name of nominee(s)</label>
                            <InputText placeholder="Input" value={formData.nomineeName as string || ''} onChange={(e) => handleInputChange('nomineeName', e.target.value)} maxLength={100} />
                        </div>
                        <div className="form-field full-width">
                            <label>Address of nominee(s)</label>
                            <InputText placeholder="Address" maxLength={200} value={formData.nomineeAddress as string || ''} onChange={(e) => handleInputChange('nomineeAddress', e.target.value)} className={errors.nomineeAddress ? 'p-invalid' : ''} />
                            {errors.nomineeAddress && <small className="p-error">{errors.nomineeAddress}</small>}
                        </div>
                    </div>

                    <div className="form-row white-bg">
                        <div className="form-field">
                            <label>Date Of Birth <span className="date-hint">(DD/MM/YYYY)</span></label>
                            <ClearableCalendar
                                value={toDateOrUndefined(formData.nomineeDob)}
                                onChange={(val) => handleInputChange('nomineeDob', val)}
                                placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY}
                                className={errors.nomineeDob ? 'p-invalid' : ''}
                            />
                            {errors.nomineeDob && <small className="p-error">{errors.nomineeDob}</small>}
                        </div>
                        <div className="form-field">
                            <label>Age</label>
                            <InputText
                                value={nomineeAge !== null ? String(nomineeAge) : (formData.nomineeAge as string || '')}
                                onChange={(e) => handleInputChange('nomineeAge', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder={nomineeAge !== null ? `${nomineeAge} (auto)` : 'Enter age'}
                                disabled={nomineeAge !== null}
                                maxLength={3}
                            />
                            <small className="field-hint">Auto-calculated if DOB is entered</small>
                        </div>
                        <div className="form-field full-width">
                            <label>Relationship with the member*</label>
                            <Dropdown value={formData.relationship} options={relationshipOptions} onChange={(e) => handleInputChange('relationship', e.value)} placeholder="Select" className={errors.relationship ? 'p-invalid' : ''} />
                            {errors.relationship && <small className="p-error">{errors.relationship}</small>}
                        </div>
                        <div className="form-field">
                            <label>Proportion*{' '}
                                <i className="pi pi-info-circle gratuity-proportion-tooltip" data-pr-tooltip="Proportion by which the gratuity will be shared" data-pr-position="top" />
                            </label>
                            <Tooltip target=".gratuity-proportion-tooltip" />
                            <div className="input-with-suffix">
                                <InputText
                                    placeholder={nominees.length === 0 ? '100' : 'e.g. 50'}
                                    value={formData.proportion as string || ''}
                                    onChange={(e) => handleInputChange('proportion', sanitiseProportion(e.target.value))}
                                    disabled={nominees.length === 0}
                                    maxLength={3}
                                    className={errors.proportion ? 'p-invalid' : ''}
                                />
                                <span className="input-suffix">%</span>
                            </div>
                            {errors.proportion && <small className="p-error">{errors.proportion}</small>}
                        </div>
                    </div>

                    <Button label="Add Nominee" icon="pi pi-plus" onClick={addNominee} className="add-nominee-btn p-button-primary" disabled={nominees.length >= 3} />

                    {nominees.map((nominee, index) => {
                        const ne = nomineeErrors[nominee.id] || {};
                        const nAgeDisplay = nominee.age || '';
                        const nDobPresent = !!nominee.dob;
                        return (
                            <div key={nominee.id} className="dynamic-nominee">
                                <button className="remove-nominee-btn" onClick={() => removeNominee(nominee.id)} title="Remove">×</button>
                                {Object.keys(ne).length > 0 && (
                                    <div className="nominee-error-banner">Complete all required fields for Nominee {index + 2}</div>
                                )}
                                <div className="form-row white-bg two-column">
                                    <div className="form-field full-width">
                                        <label>Shri/Shrimati/Kumari {index + 2}*</label>
                                        <Dropdown value={nominee.prefix} options={prefixOptions} onChange={(e) => handleNomineeChange(index, 'prefix', e.value)} placeholder="Select" className={ne.prefix ? 'p-invalid' : ''} />
                                        {ne.prefix && <small className="p-error">{ne.prefix}</small>}
                                    </div>
                                    <div className="form-field full-width">
                                        <label>Name in full with address {index + 2}*</label>
                                        <InputText placeholder="Input" value={nominee.fullName || ''} onChange={(e) => handleNomineeChange(index, 'fullName', e.target.value)} maxLength={200} className={ne.fullName ? 'p-invalid' : ''} />
                                        {ne.fullName && <small className="p-error">{ne.fullName}</small>}
                                    </div>
                                </div>
                                <div className="form-row green-bg two-column">
                                    <div className="form-field full-width">
                                        <label>Full Name {index + 2}</label>
                                        <InputText placeholder="Input" value={nominee.name || ''} onChange={(e) => handleNomineeChange(index, 'name', e.target.value)} maxLength={100} />
                                    </div>
                                    <div className="form-field full-width">
                                        <label>Address {index + 2}</label>
                                        <InputText placeholder="Address" maxLength={200} value={nominee.address || ''} onChange={(e) => handleNomineeChange(index, 'address', e.target.value)} />
                                    </div>
                                </div>
                                <div className="form-row white-bg">
                                    <div className="form-field">
                                        <label>DOB {index + 2} <span className="date-hint">(DD/MM/YYYY)</span></label>
                                        <ClearableCalendar
                                            value={toDateOrUndefined(nominee.dob)}
                                            onChange={(val) => handleNomineeChange(index, 'dob', val)}
                                            placeholder="Select" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY}
                                            className={ne.dob ? 'p-invalid' : ''}
                                        />
                                        {ne.dob && <small className="p-error">{ne.dob}</small>}
                                    </div>
                                    <div className="form-field">
                                        <label>Age {index + 2}</label>
                                        <InputText
                                            value={nAgeDisplay}
                                            onChange={(e) => handleNomineeChange(index, 'age', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                            placeholder={nDobPresent ? `${nAgeDisplay} (auto)` : 'Enter age'}
                                            disabled={nDobPresent}
                                            maxLength={3}
                                        />
                                    </div>
                                    <div className="form-field full-width">
                                        <label>Relationship {index + 2}*</label>
                                        <Dropdown value={nominee.relationship} options={relationshipOptions} onChange={(e) => handleNomineeChange(index, 'relationship', e.value)} placeholder="Select" className={ne.relationship ? 'p-invalid' : ''} />
                                        {ne.relationship && <small className="p-error">{ne.relationship}</small>}
                                    </div>
                                    <div className="form-field">
                                        <label>Proportion {index + 2}*</label>
                                        <InputText placeholder="e.g. 30" value={nominee.proportion || ''} onChange={(e) => handleNomineeChange(index, 'proportion', sanitiseProportion(e.target.value))} maxLength={3} className={ne.proportion ? 'p-invalid' : ''} />
                                        {ne.proportion && <small className="p-error">{ne.proportion}</small>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Employee Section */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Employee Name*</label>
                        <InputText disabled={isLocked('employeeName')} value={formData.employeeName as string || ''} placeholder="Auto populated" />
                    </div>
                    <div className="form-field">
                        <label>Gender*</label>
                        <Dropdown value={formData.gender} options={genderOptions} onChange={(e) => handleInputChange('gender', e.value)} placeholder="Select" className={errors.gender ? 'p-invalid' : ''} />
                        {errors.gender && <small className="p-error">{errors.gender}</small>}
                    </div>
                    <div className="form-field">
                        <label>Religion</label>
                        <InputText disabled value="NA" />
                    </div>
                    <div className="form-field">
                        <label>Marital Status*</label>
                        <Dropdown value={formData.maritalStatus} options={maritalStatusOptions} onChange={(e) => handleInputChange('maritalStatus', e.value)} placeholder="Select" className={errors.maritalStatus ? 'p-invalid' : ''} />
                        {errors.maritalStatus && <small className="p-error">{errors.maritalStatus}</small>}
                    </div>
                    <div className="form-field">
                        <label>Department</label>
                        <InputText disabled value={formData.department as string || 'Undergoing Training'} />
                    </div>
                </div>

                <div className="form-row white-bg">
                    <div className="form-field">
                        <label>Post held (Ticket/Serial No.)*</label>
                        <InputText disabled value={formData.postHeld as string || ''} placeholder="Auto populated" />
                    </div>
                    <div className="form-field">
                        <label>Date of Appointment* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.appointmentDate)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                    <div className="form-field full-width">
                        <label>Permanent Address*</label>
                        <InputText placeholder="Address" maxLength={200} value={formData.permanentAddress as string || ''} onChange={(e) => handleInputChange('permanentAddress', e.target.value)} className={errors.permanentAddress ? 'p-invalid' : ''} />
                        {errors.permanentAddress && <small className="p-error">{errors.permanentAddress}</small>}
                    </div>
                </div>

                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Place*</label>
                        <InputText disabled value={formData.place as string || ''} placeholder="Auto populated" />
                    </div>
                    <div className="form-field">
                        <label>Date* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.date)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                    <div className="form-field full-width">
                        <label>
                            Signature of the subscriber*
                            {autoSignature && formData.subscriberSignature === autoSignature && (
                                <span className="address-copy-badge">auto-filled from Form 2</span>
                            )}
                        </label>
                        <SignatureInput value={formData.subscriberSignature as string || ''} onChange={(v) => handleInputChange('subscriberSignature', v)} placeholder="Type your full name" className={errors.subscriberSignature ? 'p-invalid' : ''} />
                        {errors.subscriberSignature && <small className="p-error">{errors.subscriberSignature}</small>}
                    </div>
                </div>

                <div className="acknowledgement-section">
                    <div className={`acknowledgement-checkbox-row ${errors.acknowledgement ? 'has-error' : ''}`}>
                        <input type="checkbox" className="ack-checkbox" checked={acknowledgement}
                            onChange={(e) => { setAcknowledgement(e.target.checked); if (errors.acknowledgement) setErrors(prev => ({ ...prev, acknowledgement: '' })); }}
                            disabled={!formData.subscriberSignature} />
                        <span className="acknowledgement-label" onClick={() => { if (formData.subscriberSignature) setAcknowledgement(!acknowledgement); }}>
                            I hereby certify that the digital signature provided is true and the information furnished is accurate to the best of my knowledge.
                        </span>
                    </div>
                    {!formData.subscriberSignature && <small className="field-hint">Please enter your signature above to enable this checkbox</small>}
                    {errors.acknowledgement && <small className="p-error">{errors.acknowledgement}</small>}
                </div>

                <div className="form-row white-bg two-column">
                    <div className="form-field full-width">
                        <label>Joining location*</label>
                        <InputText disabled value={formData.joiningLocation as string || ''} placeholder="Auto populated" />
                    </div>
                    <div className="form-field full-width">
                        <label>Address of the establishment*</label>
                        <InputText disabled value={formData.establishmentAddress as string || ''} placeholder="Auto populated" />
                    </div>
                </div>

                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Designation*</label>
                        <InputText disabled value={formData.designation as string || ''} placeholder="" />
                    </div>
                    <div className="form-field">
                        <label>Date* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.finalDate)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                    <div className="form-field full-width">
                        <label>Signature of the Employer</label>
                        <InputText disabled value={formData.employerSignature as string || ''} placeholder="To be filled by employer" />
                    </div>
                </div>

                <div className="form-actions">
                    <Button label="Continue" onClick={handleContinue} className="continue-btn p-button-primary" />
                </div>
            </div>
        );
    }
);

GratuityNomination.displayName = 'GratuityNomination';
export default GratuityNomination;
